import axiosInstance from './AxiosInstance';
import {
  deriveKeys,
  unwrapDekFromPayload,
  base64ToBytes,
  zeroBytes,
} from '../crypto/credentialVaultCrypto';

export const getVaultCryptoStatus = () => axiosInstance.get('/credential-vault/crypto/status');

export const getVaultSalt = () => axiosInstance.get('/credential-vault/crypto/salt');

export const unlockVaultApi = (authHash) =>
  axiosInstance.post('/credential-vault/crypto/unlock', { authHash });

export const getPersonalVaultCryptoStatus = () =>
  axiosInstance.get('/credential-vault/crypto/personal/status');

export const getPersonalVaultSalt = () => axiosInstance.get('/credential-vault/crypto/personal/salt');

export const unlockPersonalVaultApi = (authHash) =>
  axiosInstance.post('/credential-vault/crypto/personal/unlock', { authHash });

export const setupPersonalVaultApi = (body) =>
  axiosInstance.post('/credential-vault/crypto/personal/setup', body);

export const resetPersonalVaultPasswordApi = (body) =>
  axiosInstance.post('/credential-vault/crypto/personal/reset-password', body);

/**
 * Verifies personal vault password via unlock API and returns personal DEK.
 * Caller must call zeroBytes(dek) after use unless handing off to session state.
 */
export async function unlockPersonalVaultDekFromPersonalPassword(vaultPassword) {
  if (vaultPassword == null || String(vaultPassword).trim() === '') {
    throw new Error('Personal vault password is required');
  }

  const saltRes = await getPersonalVaultSalt();
  if (!saltRes.data?.success) {
    throw new Error(saltRes.data?.message || 'Could not load personal vault salt');
  }
  const kdfSalt = saltRes.data?.data?.kdfSalt;
  if (!kdfSalt) {
    throw new Error(saltRes.data?.message || 'Personal vault not initialized');
  }

  let kek = null;
  try {
    const saltBytes = base64ToBytes(kdfSalt);
    const { kek: k, authHash } = await deriveKeys(vaultPassword, saltBytes);
    kek = k;
    const unlockRes = await unlockPersonalVaultApi(authHash);
    if (!unlockRes.data?.success) {
      throw new Error(unlockRes.data?.message || 'Incorrect personal vault password');
    }
    const enc = unlockRes.data?.data?.encryptedDek;
    if (!enc) {
      throw new Error(unlockRes.data?.message || 'Personal vault unlock failed');
    }
    const dekBytes = await unwrapDekFromPayload(enc, kek);
    await zeroBytes(kek);
    kek = null;
    return dekBytes;
  } finally {
    if (kek) {
      await zeroBytes(kek);
    }
  }
}

/**
 * Verifies master password via unlock API and returns a session DEK.
 * Caller must call zeroBytes(dek) after use.
 */
export async function unlockVaultDekFromMasterPassword(masterPassword) {
  if (masterPassword == null || String(masterPassword).trim() === '') {
    throw new Error('Master password is required');
  }

  const saltRes = await getVaultSalt();
  if (!saltRes.data?.success) {
    throw new Error(saltRes.data?.message || 'Could not load vault salt');
  }
  const kdfSalt = saltRes.data?.data?.kdfSalt;
  if (!kdfSalt) {
    throw new Error(saltRes.data?.message || 'Could not load vault salt');
  }

  let kek = null;
  try {
    const saltBytes = base64ToBytes(kdfSalt);
    const { kek: k, authHash } = await deriveKeys(masterPassword, saltBytes);
    kek = k;
    const unlockRes = await unlockVaultApi(authHash);
    if (!unlockRes.data?.success) {
      throw new Error(unlockRes.data?.message || 'Incorrect master password');
    }
    const enc = unlockRes.data?.data?.encryptedDek;
    if (!enc) {
      throw new Error(unlockRes.data?.message || 'Unlock failed');
    }
    const dekBytes = await unwrapDekFromPayload(enc, kek);
    await zeroBytes(kek);
    kek = null;
    return dekBytes;
  } finally {
    if (kek) {
      await zeroBytes(kek);
    }
  }
}

export const setupVaultApi = (body) =>
  axiosInstance.post('/credential-vault/crypto/setup', body);

export const resetMasterPasswordApi = (body) =>
  axiosInstance.post('/credential-vault/crypto/reset-master-password', body);

export const getVaultCryptoActivities = (params = {}) => {
  const q = new URLSearchParams();
  if (params.page) q.append('page', params.page);
  if (params.limit) q.append('limit', params.limit);
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return axiosInstance.get(`/credential-vault/crypto/activities${suffix}`);
};

export const listCredentials = (params = {}) => {
  const q = new URLSearchParams();
  if (params.page) q.append('page', params.page);
  if (params.limit) q.append('limit', params.limit);
  if (params.scope === 'corporate' || params.scope === 'personal') {
    q.append('scope', params.scope);
  }
  const searchTrimmed =
    params.search != null && String(params.search).trim() !== '' ? String(params.search).trim() : '';
  if (searchTrimmed) q.append('search', searchTrimmed);
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return axiosInstance.get(`/credential-vault${suffix}`);
};

export const createCredential = (data) => axiosInstance.post('/credential-vault', data);

export const getCredentialById = (id) => axiosInstance.get(`/credential-vault/${id}`);

/** Decrypted password/notes (server path) or ciphertext + IVs for client-side decrypt (ZK / personal DEK). */
export const revealCredential = (id) => axiosInstance.get(`/credential-vault/${id}/reveal`);

export const getCredentialActivities = (id, params = {}) => {
  const q = new URLSearchParams();
  if (params.page) q.append('page', params.page);
  if (params.limit) q.append('limit', params.limit);
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return axiosInstance.get(`/credential-vault/${id}/activities${suffix}`);
};

export const recordCredentialClientAction = (id, body) =>
  axiosInstance.post(`/credential-vault/${id}/activities`, body);

export const updateCredential = (id, data) => axiosInstance.put(`/credential-vault/${id}`, data);

export const deleteCredential = (id) => axiosInstance.delete(`/credential-vault/${id}`);
